<template>
  <div>
    <div class="activity-item-left-item">
      <div class="stream-avatar">
        <Avatar
          v-if="item.user"
          :image="userImage()"
          :name="item.user.name"
          :editable="false"
          :circleMode="true"
          :size="rootItem ? 40 : 28">

          <div v-if="rootItem" :class="['avatar-badge-icon', activityItemBadgeClass()]">
            <i :class="['fas', activityItemIcon()]"></i>
          </div>
        </Avatar>
        <div v-if="!item.user && rootItem" :class="['activity-item-system-avatar', activityItemBadgeClass()]">
          <i :class="['fas', activityItemIcon()]"></i>
        </div>
      </div>
    </div>

    <div :class="[rootItem ? 'activity-item-right-item' : 'activity-item-right-item-child']">
      <LinkUser
        v-if="item.user"
        :id="item.user.id"
        :name="item.user.name"
      />{{item.message}}
      <div v-if="item.extraDetails" class="well">
        {{item.extraDetails}}
      </div>
      <div class="activity-item-response">
        <span class="date">{{item.createdAt | formatCalenderDate}}</span>
        <span v-if="userHasCreatePermission(PermissionTypes.CommunicationPermissionTypes.comment) && commentsEnabled">
          &nbsp;&bull;&nbsp;
          <a href="#" @click.prevent="replyComment()">Reply</a>
        </span>
      </div>
      <LabelTags :labelList="item.labelList" />
      <div v-if="hasFileAttachments()" class="activity-item-file-container">
        <FileAttachment v-for="file in item.fileList" :key="file.id" :file="file" />
      </div>
      <button v-if="item.communicationLog" @click.prevent="viewCommunicationLog(item)" class="btn btn-default btn-xxs">
        <i class="far fa-envelope-open-text"></i> View {{formatLookupName(item.communicationLog.methodType, CommunicationMethodTypeLookup)}}
      </button>
      <div class="stream-spacer"></div>

      <ActivityCommentEditor
        ref="replyCommentEditor"
        v-if="postReply.enabled && commentsEnabled"
        v-model="postReply.commentData"
        :linkType="linkType"
        :linkId="linkId"
        :parentActivityItemId="item.id"
        @post="onPostComment()"
        @cancel="postReply.enabled=false">
      </ActivityCommentEditor>

      <ActivityLogItem
        v-for="childComment in item.children"
        :key="childComment.id"
        :item="childComment"
        :linkType="linkType"
        :linkId="linkId"
        @reply="$emit('reply')"
        @delete="$emit('delete')"
      />
    </div>

    
  </div>
</template>

<script>
import _ from 'lodash'
import UserFileMixin from '@/mixins/userFile'
import Avatar from '@/components/avatar'
import ActivityCommentEditor from '@/components/activityCommentEditor'
import CommunicationLogDialog from '@/modules/communication/views/communicationLogDialog'

import { ActivityItemTypes, ActivityLogTypes, LinkTypes, CommunicationMethodTypeLookup } from '@/store/types'
import { mapState } from "vuex"

export default {
  name: 'ActivityLogItem',
  mixins: [UserFileMixin],
  components: {
    Avatar,
    ActivityCommentEditor
  },
  props: {
    item: Object,
    linkType: String,
    linkId: [String, Number],
    rootItem: Boolean,
    commentsEnabled: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapState({
      profileUserId: state => state.profile.profileUser.id
    })
  },
  created() {
    this.CommunicationMethodTypeLookup = CommunicationMethodTypeLookup
  },
  data() {
    return {
      postReply: {
        enabled: false,
        commentData: {}
      }
    }
  },
  methods: {
    userImage() {
      if (this.item.user && this.item.user.profileURL != "")
        return this.item.user.profileURL
      return "/glass/img/user-overview-male.png"
    },
    replyComment() {
      this.postReply.enabled = true
      this.postReply.commentData = {}
      this.$nextTick(() => {
        this.$refs.replyCommentEditor.select()
      })
    },
    // JB: 20/07/2020 - Ken wanted Delete comment to be removed
    // Stored here for future use if needed
    deleteComment() {
      this.confirmDeleteMessage('Delete Comment?', 'This will delete the comment and any replies. This cannot be undone')
      .then(success => {
        if(success) {
          this.$apiService.coreService.deleteComment(this.item.id).then(response => {
            this.successToast('Comment Removed')
            this.$emit('delete')
          })
        }
      })
      .catch(err => {
        this.displayNetworkError(err, 'Error', 'Error while deleting comment')
      })
    },
    onPostComment() {
      this.postReply.enabled = false
      this.$emit('reply')
    },
    ownComment() {
      if(this.item.user) {
        return this.item.user.id == this.profileUserId
      }
      return false
    },
    itemIsComment() {
      return this.item.type == ActivityItemTypes.comment
    },
    hasFileAttachments() {
      return _.isArray(this.item.fileList) && this.item.fileList.length > 0
    },
    viewCommunicationLog(item)  {
      if(!item.communicationLog)
        return
      this.displayModal(CommunicationLogDialog, { 
        communicationLogId: item.communicationLog.id
      })
      .then(() => { })
    },
    fileIconClass(fileItem) {
      return this.iconForUserFile(fileItem.mimeType)
    },
    activityItemIcon() {
      if(this.item.type == ActivityItemTypes.comment)
        return 'fa-comment'
      
      if(this.item.type == ActivityItemTypes.activityLog) {
        switch (this.item.activityType) {
          case ActivityLogTypes.created:
            return "fa-plus";
          case ActivityLogTypes.updated:
            return "fa-pencil";
          case ActivityLogTypes.deleted:
            return "fa-trash";
          case ActivityLogTypes.archived:
            return "fa-archive";
          case ActivityLogTypes.restored:
            return "fa-trash-restore";
          case ActivityLogTypes.status:
            return "fa-info-circle";
          case ActivityLogTypes.action:
            return "fa-tasks";
          case ActivityLogTypes.error:
            return "fa-exclamation-triangle";
          case ActivityLogTypes.approved:
            return "fa-thumbs-up";
          case ActivityLogTypes.denied:
            return "fa-thumbs-down";
          case ActivityLogTypes.emailed:
            return "fa-paper-plane";
          case ActivityLogTypes.printed:
            return "fa-print";
        }
      }
      return "fa-pencil";
    },
    activityItemBadgeClass() {
      if(this.item.type == ActivityItemTypes.comment)
        return 'bg-primary'
      
      if(this.item.type == ActivityItemTypes.activityLog) {
        switch (this.item.activityType) {
          case ActivityLogTypes.created:
            return "bg-primary";
          case ActivityLogTypes.updated:
            return "bg-default";
          case ActivityLogTypes.deleted:
            return "bg-danger";
          case ActivityLogTypes.archived:
            return "bg-warning";
          case ActivityLogTypes.restored:
            return "bg-success";
          case ActivityLogTypes.status:
            return "bg-success";
          case ActivityLogTypes.action:
            return "bg-action";
          case ActivityLogTypes.error:
            return "bg-danger";
          case ActivityLogTypes.approved:
            return "bg-success";
          case ActivityLogTypes.denied:
            return "bg-danger";
          case ActivityLogTypes.emailed:
            return "bg-success";
          case ActivityLogTypes.printed:
            return "bg-success";
        }
      }
      return "";
    },
  }
}
</script>